import Building from '../../entities/Building'
import Floor from '../../entities/Floor'
import { FloorData } from './Overlay'

/**
 * We no longer use "quadrants" to define each apartment. Instead, each apartment
 * is described by a 2D polygon (outline) in grid‐coordinate space.  When fed into
 * the Overlay3D component (which multiplies each x/y by `cellSize` and then
 * extrudes by `height`), these outlines will produce L-, +-, dented-, or any
 * arbitrary‐shape footprints without interior grid lines.
 *
 * Below, xGridAxisSize = 8 and yGridAxisSize = 10 are still the underlying
 * building grid, but we write each apartment's outline "by hand" as a rectangle
 * corresponding to the same 3×3 splits used previously—just without computing
 * them at runtime.
 *
 * Block sizes (in cells):
 *   xBlockSizes = [3, 3, 2]   →  x ranges:  [0→3), [3→6), [6→8)
 *   yBlockSizes = [4, 3, 3]   →  y ranges:  [0→4), [4→7), [7→10)
 *
 * Apartments A1 through A9 map to those 3×3 blocks—skipping A5, which is the
 * center block row=1, col=1.  Each outline is given in grid‐units; Overlay3D
 * will multiply by `cellSize` when placing them.
 */

const xGridAxisSize = 8
const yGridAxisSize = 10
const baseHeight = 0.62

const apartmentLastFloor = {
  C1: [
    { x: 0.3, y: 3.5 },
    { x: 4.15, y: 3.5 },
  ],
  C2: [
    { x: 4.15, y: 3.5 },
    { x: 5.5, y: 3.5 },
  ],
  C21: [
    { x: 5.5, y: 3.5 },
    { x: 6.75, y: 7.4 },
  ],
}

const apartmentConfig: Record<string, ApartmentConfig> = {
  B1: {
    footprint: [
      { x: 0.3, y: 2.5 },
      { x: 1.716, y: 2.548 },
    ],
    unavailableOnFloors: [1, 2],
  },

  B2: {
    footprint: [
      { x: 1.716, y: 2.548 },
      { x: 4.0, y: 2.626 },
    ],
    unavailableOnFloors: [1, 2, 3],
  },
  B3: {
    footprint: [
      { x: 4.0, y: 2.626 },
      { x: 6.2, y: 2.7 },
    ],
    unavailableOnFloors: [1, 3],
  },

  B31: {
    footprint: [
      { x: 6.2, y: 2.7 },
      { x: 7.040, y: 5.276 },
    ],
    unavailableOnFloors: [1, 3],
  },

  B4: {
    footprint: [
      { x: 7.040, y: 5.276 },
      { x: 7.7, y: 7.3 },
    ],
    unavailableOnFloors: [1, 2],
  },
}

const apartmentFirstFloor = {
  A1: [
    { x: 0.3, y: 2.5 },
    { x: 3.250, y: 2.6 },
  ],

  A2: [
    { x: 3.250, y: 2.6 },
    { x: 6.2, y: 2.7 },
  ],

  A21: [
    { x: 6.2, y: 2.7 },
    { x: 7.040, y: 5.276 },
  ],

  A3: [
    { x: 7.040, y: 5.276 },
    { x: 7.7, y: 7.3 },
  ],
}

const apartmentGroupsLast: Record<string, string[]> = {
  C2: ['C2', 'C21'],
  C21: ['C2', 'C21'],
}

const apartmentGroupsMiddle: Record<string, string[]> = {
  B3: ['B3', 'B31'],
  B31: ['B3', 'B31'],
}

const apartmentGroupsFirst: Record<string, string[]> = {
  A2: ['A2', 'A21'],
  A21: ['A2', 'A21'],
}

// Helper function to get the appropriate apartment groups based on floor index
const getApartmentGroups = (floorIndex: number): Record<string, string[]> => {
  if (floorIndex === 0) {
    return apartmentGroupsFirst
  } else if (floorIndex === 4) { // Last floor (5th floor, index 4)
    return apartmentGroupsLast
  } else {
    return apartmentGroupsMiddle
  }
}

// Helper function to get all apartments in the same group
export const getApartmentGroup = (aptId: string, floorIndex?: number): string[] => {
  if (floorIndex === undefined) {
    // Fallback to middle floor behavior for backward compatibility
    return apartmentGroupsMiddle[aptId] || [aptId]
  }

  const groups = getApartmentGroups(floorIndex)
  return groups[aptId] || [aptId]
}

// Helper function to get the main apartment ID for a group (the first one alphabetically)
export const getMainApartmentId = (aptId: string, floorIndex?: number): string => {
  const group = getApartmentGroup(aptId, floorIndex)
  return group.sort()[0] // Return the first one alphabetically
}

// ===== APARTMENT CONFIGURATION =====
// Each apartment has footprint coordinates and availability settings
interface ApartmentConfig {
  footprint: { x: number; y: number }[];
  unavailableOnFloors: number[];
}

// Extract just the footprints for backward compatibility
const apartmentFootprints = Object.fromEntries(
  Object.entries(apartmentConfig).map(([id, config]) => [id, config.footprint])
)

// Helper function to check apartment availability
const getApartmentAvailability = (
  floorIndex: number,
  aptId: string
): boolean => {
  const apt = apartmentConfig[aptId]
  if (!apt) return true
  return !apt.unavailableOnFloors.includes(floorIndex)
}

const apartmentBuilding: Building = {
  id: 'ApartmentBuilding',
  name: 'Apartment Building',
  floors: Array.from({ length: 5 }, (_, idx) => {
    const floorIndex = idx
    // First floor is slightly taller:
    let floorHeight = baseHeight

    if (idx === 0) {
      floorHeight = baseHeight * 1.25
    }

    if (idx === 4) {
      floorHeight = baseHeight * 1.3
    }

    // Convert our footprint dictionary into an array of Floor.apartments,
    // each with id + outline.  No "quadrants" any more.
    let floorApartments = Object.entries(apartmentFootprints).map(
      ([id, outline]) => ({
        id,
        outline, // The component will scale each (x, y) by cellSize
        // Set apartment availability - false makes them invisible
        availability: getApartmentAvailability(idx, id),
      })
    )

    // Override apartments for specific floors
    if (idx === 0) {
      floorApartments = Object.entries(apartmentFirstFloor).map(
        ([id, outline]) => ({
          id,
          outline,
          availability: id !== 'A1' && id !== 'A3'
        })
      )
    }

    if (idx === 4) {
      floorApartments = Object.entries(apartmentLastFloor).map(
        ([id, outline]) => ({
          id,
          outline,
          availability: true, // All apartments on last floor are available
        })
      )
    }

    return {
      id: `Floor${floorIndex + 1}`,
      floorIndex,
      // We keep the grid‐size on the Floor object even though it's unused downstream:
      xGridAxisSize,
      yGridAxisSize,
      height: floorHeight,
      // Swap out the old quadrants‐based array for our new outline‐based one:
      apartments: floorApartments,
    } as FloorData
  }),
}

export default apartmentBuilding
